import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeftIcon, SearchIcon, PlusIcon, ListIcon, XIcon, PackageIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Select } from '../components/Select';
import { RichTextEditor } from '../components/RichTextEditor';
import { Customer } from '../components/CustomerCard';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
interface StandardMaterial {
  id: string;
  name: string;
  price?: number;
}
interface AddTaskPageProps {
  customers: Customer[];
  nextTaskId: string;
  onAdd: (task: {
    taskId: string;
    title: string;
    customerId: string;
    description: string;
    status: 'todo' | 'in-progress' | 'done' | 'planned' | 'pending';
    category: 'Ladeboks' | 'Andet';
    chargerType?: 'Zaptec 1' | 'Zaptec 2' | 'Andet';
    taskType: 'Tilbudsopgave' | 'Regningsopgave';
    agreedPlanning?: string;
    agreedInstallation?: string;
    carDeliveryDate?: string;
    hours?: number;
    materialPrice?: number;
    hourlyRate?: number;
    quotePrice?: number;
    totalPrice?: number;
    invoiced: boolean;
    subTasks?: string;
    materials?: string[];
    workLog?: Array<{
      date: string;
      description: string;
    }>;
  }) => void;
  onBack: () => void;
  initialCustomerId?: string;
}
export function AddTaskPage({
  customers,
  nextTaskId,
  onAdd,
  onBack,
  initialCustomerId
}: AddTaskPageProps) {
  const [title, setTitle] = useState('');
  const [customerId, setCustomerId] = useState(initialCustomerId || '');
  const [customerSearch, setCustomerSearch] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<'todo' | 'in-progress' | 'done' | 'planned' | 'pending'>('in-progress');
  const [category, setCategory] = useState<'Ladeboks' | 'Andet'>('Ladeboks');
  const [chargerType, setChargerType] = useState<'Zaptec 1' | 'Zaptec 2' | 'Andet'>('Zaptec 1');
  const [taskType, setTaskType] = useState<'Tilbudsopgave' | 'Regningsopgave'>('Tilbudsopgave');
  const [agreedPlanning, setAgreedPlanning] = useState('');
  const [agreedPlanningTime, setAgreedPlanningTime] = useState('09:00');
  const [agreedInstallation, setAgreedInstallation] = useState('');
  const [agreedInstallationTime, setAgreedInstallationTime] = useState('09:00');
  const [carDeliveryDate, setCarDeliveryDate] = useState('');
  const [hours, setHours] = useState('');
  const [materialPrice, setMaterialPrice] = useState('');
  const [hourlyRate, setHourlyRate] = useState('');
  const [quotePrice, setQuotePrice] = useState('');
  const [invoiced, setInvoiced] = useState('no');
  const [subTasks, setSubTasks] = useState('');
  const [materialInput, setMaterialInput] = useState('');
  const [materialPriceInput, setMaterialPriceInput] = useState('');
  const [materials, setMaterials] = useState<string[]>([]);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkInput, setBulkInput] = useState('');
  const [bulkPrice, setBulkPrice] = useState('');
  const [standardMaterials, setStandardMaterials] = useState<StandardMaterial[]>([]);
  const [chargerPrices, setChargerPrices] = useState({
    'Zaptec 1': 1046.3,
    'Zaptec 2': 4321.54,
    Andet: 0
  });
  // Load standard materials
  useEffect(() => {
    const docRef = doc(db, 'settings', 'standardMaterials');
    const unsubscribe = onSnapshot(docRef, docSnapshot => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        setStandardMaterials(data.items || []);
      }
    }, error => {
      console.error('Error loading standard materials:', error);
    });
    return () => unsubscribe();
  }, []);
  useEffect(() => {
    const loadPrices = async () => {
      try {
        const docRef = doc(db, 'settings', 'chargerPrices');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setChargerPrices(docSnap.data() as any);
        }
      } catch (error) {
        console.error('Error loading charger prices:', error);
      }
    };
    loadPrices();
  }, []);
  useEffect(() => {
    if (category === 'Ladeboks' && chargerType !== 'Andet') {
      setMaterialPrice(chargerPrices[chargerType].toString());
    }
  }, [chargerType, category, chargerPrices]);
  const filteredCustomers = useMemo(() => {
    if (!customerSearch.trim()) return customers;
    const query = customerSearch.toLowerCase();
    return customers.filter(customer => customer.name.toLowerCase().includes(query) || customer.customerId.toLowerCase().includes(query) || customer.phone.toLowerCase().includes(query) || customer.email?.toLowerCase().includes(query));
  }, [customers, customerSearch]);
  const calculations = useMemo(() => {
    const material = materialPrice ? parseFloat(materialPrice) : 0;
    const rate = hourlyRate ? parseFloat(hourlyRate) : 0;
    const time = hours ? parseFloat(hours) : 0;
    const quote = quotePrice ? parseFloat(quotePrice) : 0;
    if (taskType === 'Tilbudsopgave') {
      const profit = quote - material;
      const realHourlyRate = time > 0 ? profit / time : null;
      const laborPrice = quote - material;
      return {
        totalPrice: quote,
        profit: quote > 0 || material > 0 ? profit : null,
        realHourlyRate: realHourlyRate,
        laborPrice: quote > 0 ? laborPrice : null
      };
    } else {
      const laborCost = rate * time;
      const totalPrice = laborCost + material;
      const profit = laborCost - material;
      const realHourlyRate = time > 0 ? profit / time : null;
      return {
        totalPrice: totalPrice,
        profit: rate > 0 && time > 0 || material > 0 ? profit : null,
        realHourlyRate: realHourlyRate,
        laborPrice: rate > 0 && time > 0 ? laborCost : null
      };
    }
  }, [taskType, quotePrice, materialPrice, hourlyRate, hours]);
  const handleAddMaterial = () => {
    if (materialInput.trim()) {
      setMaterials([...materials, materialInput.trim()]);
      if (materialPriceInput.trim()) {
        const currentPrice = materialPrice ? parseFloat(materialPrice) : 0;
        const additionalPrice = parseFloat(materialPriceInput);
        const newTotalPrice = currentPrice + additionalPrice;
        setMaterialPrice(newTotalPrice.toString());
      }
      setMaterialInput('');
      setMaterialPriceInput('');
    }
  };
  const handleAddPresetMaterial = (preset: StandardMaterial) => {
    setMaterials([...materials, preset.name]);
    if (preset.price !== undefined) {
      const currentPrice = materialPrice ? parseFloat(materialPrice) : 0;
      const newTotalPrice = currentPrice + preset.price;
      setMaterialPrice(newTotalPrice.toString());
    }
  };
  const handleAddBulkMaterials = () => {
    if (bulkInput.trim()) {
      const newMaterials = bulkInput.split('\n').map(line => line.trim()).filter(line => line.length > 0);
      if (newMaterials.length > 0) {
        setMaterials([...materials, ...newMaterials]);
        if (bulkPrice.trim()) {
          const currentPrice = materialPrice ? parseFloat(materialPrice) : 0;
          const additionalPrice = parseFloat(bulkPrice);
          const newTotalPrice = currentPrice + additionalPrice;
          setMaterialPrice(newTotalPrice.toString());
        }
        setBulkInput('');
        setBulkPrice('');
        setBulkMode(false);
      }
    }
  };
  const handleRemoveMaterial = (index: number) => {
    setMaterials(materials.filter((_, i) => i !== index));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !customerId) return;
    const planningDateTime = agreedPlanning ? `${agreedPlanning}T${agreedPlanningTime}:00` : undefined;
    const installationDateTime = agreedInstallation ? `${agreedInstallation}T${agreedInstallationTime}:00` : undefined;
    onAdd({
      taskId: nextTaskId,
      title,
      customerId,
      description,
      status,
      category,
      chargerType: category === 'Ladeboks' ? chargerType : undefined,
      taskType,
      agreedPlanning: planningDateTime,
      agreedInstallation: installationDateTime,
      carDeliveryDate: carDeliveryDate || undefined,
      hours: hours ? parseFloat(hours) : undefined,
      materialPrice: materialPrice ? parseFloat(materialPrice) : undefined,
      hourlyRate: hourlyRate ? parseFloat(hourlyRate) : undefined,
      quotePrice: quotePrice ? parseFloat(quotePrice) : undefined,
      totalPrice: calculations.totalPrice || undefined,
      invoiced: invoiced === 'yes',
      subTasks: subTasks || undefined,
      materials: materials.length > 0 ? materials : undefined
    });
    onBack();
  };
  return <div className="min-h-screen bg-stone-50 py-8" data-id="element-1056">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1057">
        <button onClick={onBack} className="flex items-center gap-2 text-stone-500 hover:text-stone-900 transition-colors mb-6" data-id="element-1058">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-1059" />
          <span className="font-medium" data-id="element-1060">Tilbage</span>
        </button>

        <div className="mb-8" data-id="element-1061">
          <h1 className="text-3xl font-bold tracking-tight text-stone-900" data-id="element-1062">
            Tilføj ny opgave
          </h1>
          <p className="text-stone-500 mt-2" data-id="element-1063">
            Opgave-ID:{' '}
            <span className="font-medium text-teal-600" data-id="element-1064">{nextTaskId}</span>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8" data-id="element-1065">
          {/* Grundlæggende information */}
          <div className="bg-white rounded-2xl border border-stone-200 p-6 sm:p-8 shadow-sm" data-id="element-1066">
            <h2 className="text-xl font-semibold text-stone-900 mb-6" data-id="element-1067">
              Grundlæggende information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1068">
              <Input label="Opgavetitel" value={title} onChange={e => setTitle(e.target.value)} placeholder="Indtast opgavetitel" required data-id="element-1069" />

              <div data-id="element-1070">
                <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-1071">
                  Kunde <span className="text-red-500" data-id="element-1072">*</span>
                </label>
                <div className="relative mb-2" data-id="element-1073">
                  <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" data-id="element-1074" />
                  <input type="text" placeholder="Søg efter kunde..." value={customerSearch} onChange={e => setCustomerSearch(e.target.value)} className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-1075" />
                </div>
                <select value={customerId} onChange={e => setCustomerId(e.target.value)} required className="w-full px-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-1076">
                  <option value="" data-id="element-1077">Vælg en kunde</option>
                  {filteredCustomers.map(c => <option key={c.id} value={c.id} data-id="element-1078">
                      {c.name} ({c.customerId})
                    </option>)}
                </select>
                {customerSearch && filteredCustomers.length === 0 && <p className="text-sm text-stone-500 mt-1" data-id="element-1079">
                    Ingen kunder matcher søgningen
                  </p>}
              </div>
            </div>

            <div className="mt-6" data-id="element-1080">
              <RichTextEditor label="Beskrivelse" value={description} onChange={setDescription} placeholder="Beskriv opgaven" rows={4} data-id="element-1081" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6" data-id="element-1082">
              <Select label="Status" value={status} onChange={e => setStatus(e.target.value as 'todo' | 'in-progress' | 'done' | 'planned' | 'pending')} options={[{
              value: 'planned',
              label: 'Planlagt'
            }, {
              value: 'pending',
              label: 'Afventende'
            }, {
              value: 'in-progress',
              label: 'I gang'
            }, {
              value: 'done',
              label: 'Afsluttet'
            }]} data-id="element-1083" />

              <Select label="Faktureret" value={invoiced} onChange={e => setInvoiced(e.target.value)} options={[{
              value: 'no',
              label: 'Nej'
            }, {
              value: 'yes',
              label: 'Ja'
            }]} data-id="element-1084" />
            </div>
          </div>

          {/* Datoer */}
          <div className="bg-white rounded-2xl border border-stone-200 p-6 sm:p-8 shadow-sm" data-id="element-1085">
            <h2 className="text-xl font-semibold text-stone-900 mb-6" data-id="element-1086">
              Datoer
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1087">
              <div data-id="element-1088">
                <Input label="Aftalt projektering" type="date" value={agreedPlanning} onChange={e => setAgreedPlanning(e.target.value)} data-id="element-1089" />
                {agreedPlanning && <div className="mt-3" data-id="element-1090">
                    <Input label="Tidspunkt" type="time" value={agreedPlanningTime} onChange={e => setAgreedPlanningTime(e.target.value)} data-id="element-1091" />
                  </div>}
              </div>

              <div data-id="element-1092">
                <Input label="Aftalt installation" type="date" value={agreedInstallation} onChange={e => setAgreedInstallation(e.target.value)} data-id="element-1093" />
                {agreedInstallation && <div className="mt-3" data-id="element-1094">
                    <Input label="Tidspunkt" type="time" value={agreedInstallationTime} onChange={e => setAgreedInstallationTime(e.target.value)} data-id="element-1095" />
                  </div>}
              </div>
            </div>

            {category === 'Ladeboks' && <div className="mt-6" data-id="element-1096">
                <Input label="Leveringsdato på bil (valgfrit)" type="date" value={carDeliveryDate} onChange={e => setCarDeliveryDate(e.target.value)} data-id="element-1097" />
                <p className="text-sm text-stone-500 mt-1.5" data-id="element-1098">
                  Angiv hvornår bilen leveres til kunden
                </p>
              </div>}
          </div>

          {/* Pris og fakturering */}
          <div className="bg-white rounded-2xl border border-stone-200 p-6 sm:p-8 shadow-sm" data-id="element-1099">
            <h2 className="text-xl font-semibold text-stone-900 mb-6" data-id="element-1100">
              Pris og fakturering
            </h2>

            <div className="space-y-6 mb-8" data-id="element-1101">
              <div data-id="element-1102">
                <label className="block text-sm font-medium text-stone-700 mb-2" data-id="element-1103">
                  Kategori
                </label>
                <div className="flex p-1 bg-stone-50 rounded-xl border border-stone-200 w-fit" data-id="element-1104">
                  {(['Ladeboks', 'Andet'] as const).map(cat => <button key={cat} type="button" onClick={() => setCategory(cat)} className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all ${category === cat ? 'bg-white text-stone-900 shadow-sm border border-stone-200' : 'text-stone-500 hover:text-stone-700 border border-transparent'}`} data-id="element-1105">
                      {cat === 'Andet' ? 'Alm. Installation' : cat}
                    </button>)}
                </div>
              </div>

              {category === 'Ladeboks' && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} className="space-y-2" data-id="element-1106">
                  <label className="block text-sm font-medium text-stone-700" data-id="element-1107">
                    Ladeboks type
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-1108">
                    {(['Zaptec 1', 'Zaptec 2', 'Andet'] as const).map(type => <button key={type} type="button" onClick={() => setChargerType(type)} className={`p-4 rounded-xl border text-left transition-all ${chargerType === type ? 'bg-teal-50 border-teal-500 ring-1 ring-teal-500' : 'bg-white border-stone-200 hover:border-stone-300'}`} data-id="element-1109">
                          <div className="font-medium text-stone-900" data-id="element-1110">
                            {type}
                          </div>
                          {type !== 'Andet' && <div className="text-sm text-stone-500 mt-1" data-id="element-1111">
                              {chargerPrices[type].toLocaleString('da-DK')} kr
                            </div>}
                        </button>)}
                  </div>
                </motion.div>}

              <div className="pt-4 border-t border-stone-100" data-id="element-1112">
                <label className="block text-sm font-medium text-stone-700 mb-2" data-id="element-1113">
                  Opgavetype
                </label>
                <div className="flex p-1 bg-stone-50 rounded-xl border border-stone-200 w-fit" data-id="element-1114">
                  {(['Tilbudsopgave', 'Regningsopgave'] as const).map(type => <button key={type} type="button" onClick={() => setTaskType(type)} className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all ${taskType === type ? 'bg-white text-stone-900 shadow-sm border border-stone-200' : 'text-stone-500 hover:text-stone-700 border border-transparent'}`} data-id="element-1115">
                        {type}
                      </button>)}
                </div>
              </div>
            </div>

            <div className="bg-stone-50 rounded-2xl p-6 border border-stone-200" data-id="element-1116">
              <h3 className="text-sm font-semibold text-stone-900 uppercase tracking-wide mb-6" data-id="element-1117">
                Timer & Pris
              </h3>

              {taskType === 'Tilbudsopgave' ? <div className="space-y-6" data-id="element-1118">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1119">
                    <div data-id="element-1120">
                      <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-1121">
                        Tilbudspris (kr) <span className="text-red-500" data-id="element-1122">*</span>
                      </label>
                      <input type="number" step="0.01" value={quotePrice} onChange={e => setQuotePrice(e.target.value)} placeholder="f.eks. 8000" required className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-1123" />
                      <p className="text-xs text-stone-500 mt-1.5" data-id="element-1124">
                        Den samlede pris kunden skal betale
                      </p>
                    </div>

                    <div data-id="element-1125">
                      <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-1126">
                        Materialer pris (kr)
                      </label>
                      <input type="number" step="0.01" value={materialPrice} onChange={e => setMaterialPrice(e.target.value)} placeholder="f.eks. 4400" disabled={category === 'Ladeboks' && chargerType !== 'Andet'} className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all disabled:bg-stone-100 disabled:text-stone-500" data-id="element-1127" />
                      {category === 'Ladeboks' && chargerType !== 'Andet' ? <p className="text-xs text-stone-500 mt-1.5" data-id="element-1128">
                          Udfyldes automatisk baseret på ladeboks type
                        </p> : <p className="text-xs text-stone-500 mt-1.5" data-id="element-1129">
                          Dine udgifter til materialer
                        </p>}
                    </div>
                  </div>

                  <div className="pt-4 border-t border-stone-200" data-id="element-1130">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1131">
                      <div data-id="element-1132">
                        <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-1133">
                          Timer brugt
                        </label>
                        <input type="number" step="0.5" value={hours} onChange={e => setHours(e.target.value)} placeholder="f.eks. 4" className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-1134" />
                        <p className="text-xs text-stone-500 mt-1.5" data-id="element-1135">
                          Bruges til at udregne din reelle timepris
                        </p>
                      </div>

                      <div className="bg-white p-4 rounded-xl border border-stone-200 flex flex-col justify-center gap-3" data-id="element-1136">
                        <div data-id="element-1137">
                          <p className="text-xs font-medium text-stone-500 uppercase tracking-wide" data-id="element-1138">
                            Samlet pris
                          </p>
                          <p className="text-xl font-bold text-stone-900 mt-0.5" data-id="element-1139">
                            {calculations.totalPrice.toLocaleString('da-DK', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}{' '}
                            kr
                          </p>
                        </div>
                        <div className="pt-3 border-t border-stone-100" data-id="element-1140">
                          <p className="text-xs font-medium text-stone-500 uppercase tracking-wide" data-id="element-1141">
                            Pris kun for timer
                          </p>
                          <p className="text-xl font-bold text-blue-700 mt-0.5" data-id="element-1142">
                            {(calculations.laborPrice ?? 0).toLocaleString('da-DK', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}{' '}
                            kr
                          </p>
                          <p className="text-xs text-stone-400 mt-1" data-id="element-1143">
                            Tilbudspris − Materialer
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div> : <div className="space-y-6" data-id="element-1144">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1145">
                    <div data-id="element-1146">
                      <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-1147">
                        Timepris (kr) <span className="text-red-500" data-id="element-1148">*</span>
                      </label>
                      <input type="number" step="0.01" value={hourlyRate} onChange={e => setHourlyRate(e.target.value)} placeholder="f.eks. 750" required className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-1149" />
                      <p className="text-xs text-stone-500 mt-1.5" data-id="element-1150">
                        Din pris per time
                      </p>
                    </div>

                    <div data-id="element-1151">
                      <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-1152">
                        Materialer pris (kr)
                      </label>
                      <input type="number" step="0.01" value={materialPrice} onChange={e => setMaterialPrice(e.target.value)} placeholder="f.eks. 4400" disabled={category === 'Ladeboks' && chargerType !== 'Andet'} className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all disabled:bg-stone-100 disabled:text-stone-500" data-id="element-1153" />
                      {category === 'Ladeboks' && chargerType !== 'Andet' ? <p className="text-xs text-stone-500 mt-1.5" data-id="element-1154">
                          Udfyldes automatisk baseret på ladeboks type
                        </p> : <p className="text-xs text-stone-500 mt-1.5" data-id="element-1155">
                          Dine udgifter til materialer
                        </p>}
                    </div>
                  </div>

                  <div className="pt-4 border-t border-stone-200" data-id="element-1156">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1157">
                      <div data-id="element-1158">
                        <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-1159">
                          Timer brugt <span className="text-red-500" data-id="element-1160">*</span>
                        </label>
                        <input type="number" step="0.5" value={hours} onChange={e => setHours(e.target.value)} placeholder="f.eks. 4" required className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-1161" />
                        <p className="text-xs text-stone-500 mt-1.5" data-id="element-1162">
                          Antal timer brugt på opgaven
                        </p>
                      </div>

                      <div className="bg-white p-4 rounded-xl border border-stone-200 flex flex-col justify-center gap-3" data-id="element-1163">
                        <div data-id="element-1164">
                          <p className="text-xs font-medium text-stone-500 uppercase tracking-wide" data-id="element-1165">
                            Samlet pris
                          </p>
                          <p className="text-xl font-bold text-stone-900 mt-0.5" data-id="element-1166">
                            {calculations.totalPrice.toLocaleString('da-DK', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}{' '}
                            kr
                          </p>
                        </div>
                        <div className="pt-3 border-t border-stone-100" data-id="element-1167">
                          <p className="text-xs font-medium text-stone-500 uppercase tracking-wide" data-id="element-1168">
                            Pris kun for timer
                          </p>
                          <p className="text-xl font-bold text-blue-700 mt-0.5" data-id="element-1169">
                            {(calculations.laborPrice ?? 0).toLocaleString('da-DK', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}{' '}
                            kr
                          </p>
                          <p className="text-xs text-stone-400 mt-1" data-id="element-1170">
                            Timepris × Timer
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>}

              {/* Summary Results */}
              <div className="mt-8 pt-6 border-t border-stone-200 grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1171">
                {calculations.realHourlyRate !== null && <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 shadow-sm" data-id="element-1172">
                    <p className="text-sm font-medium text-blue-800 mb-1" data-id="element-1173">
                      Reel timepris
                    </p>
                    <p className="text-2xl font-bold text-blue-700" data-id="element-1174">
                      {calculations.realHourlyRate.toLocaleString('da-DK', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}{' '}
                      kr/t
                    </p>
                  </div>}
              </div>
            </div>
          </div>

          {/* Materialer */}
          <div className="bg-white rounded-2xl border border-stone-200 p-6 sm:p-8 shadow-sm" data-id="element-1175">
            <div className="flex items-center justify-between mb-6" data-id="element-1176">
              <h2 className="text-xl font-semibold text-stone-900" data-id="element-1177">
                Materialer
              </h2>
              <button type="button" onClick={() => setBulkMode(!bulkMode)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors" data-id="element-1178">
                <ListIcon className="w-4 h-4" data-id="element-1179" />
                {bulkMode ? 'Enkelt tilføjelse' : 'Tilføj ordre'}
              </button>
            </div>

            <AnimatePresence mode="wait" data-id="element-1180">
              {bulkMode ? <motion.div key="bulk" initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} className="space-y-4" data-id="element-1181">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4" data-id="element-1182">
                    <p className="text-sm text-blue-900 font-medium mb-1" data-id="element-1183">
                      📦 Tilføj stor ordre
                    </p>
                    <p className="text-sm text-blue-700" data-id="element-1184">
                      Skriv ét materiale per linje. Prisen lægges automatisk til
                      materialer pris.
                    </p>
                  </div>

                  <textarea value={bulkInput} onChange={e => setBulkInput(e.target.value)} placeholder="Kabel 10m&#10;Stikkontakt&#10;Sikring 16A&#10;Kabelkanal 2m&#10;..." rows={6} className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none font-mono text-sm" data-id="element-1185" />

                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-5" data-id="element-1186">
                    <label className="block text-sm font-medium text-amber-900 mb-2" data-id="element-1187">
                      💰 Pris for denne ordre (kr)
                    </label>
                    <input type="number" step="0.01" value={bulkPrice} onChange={e => setBulkPrice(e.target.value)} placeholder="f.eks. 2500" className="w-full px-4 py-3 rounded-lg border border-amber-300 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all" data-id="element-1188" />
                    {bulkPrice && materialPrice && <div className="mt-4 pt-4 border-t border-amber-200" data-id="element-1189">
                        <div className="flex items-center justify-between text-base" data-id="element-1190">
                          <span className="text-amber-700" data-id="element-1191">
                            Nuværende materialer pris:
                          </span>
                          <span className="font-medium text-amber-900" data-id="element-1192">
                            {parseFloat(materialPrice).toLocaleString('da-DK', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}{' '}
                            kr
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-base mt-2" data-id="element-1193">
                          <span className="text-amber-700" data-id="element-1194">Ordre pris:</span>
                          <span className="font-medium text-amber-900" data-id="element-1195">
                            +
                            {parseFloat(bulkPrice).toLocaleString('da-DK', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}{' '}
                            kr
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-lg mt-3 pt-3 border-t border-amber-300" data-id="element-1196">
                          <span className="font-semibold text-amber-900" data-id="element-1197">
                            Ny total:
                          </span>
                          <span className="font-bold text-amber-900" data-id="element-1198">
                            {(parseFloat(materialPrice) + parseFloat(bulkPrice)).toLocaleString('da-DK', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}{' '}
                            kr
                          </span>
                        </div>
                      </div>}
                  </div>

                  <div className="flex gap-4" data-id="element-1199">
                    <Button type="button" onClick={handleAddBulkMaterials} variant="primary" className="flex-1 flex items-center justify-center gap-2 py-3" disabled={!bulkInput.trim()} data-id="element-1200">
                      <PlusIcon className="w-5 h-5" data-id="element-1201" />
                      Tilføj alle materialer
                      {bulkPrice && ` (+${parseFloat(bulkPrice).toLocaleString('da-DK')} kr)`}
                    </Button>
                    <Button type="button" onClick={() => {
                  setBulkInput('');
                  setBulkPrice('');
                  setBulkMode(false);
                }} variant="secondary" className="py-3 px-6" data-id="element-1202">
                      Annuller
                    </Button>
                  </div>
                </motion.div> : <motion.div key="single" initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} className="space-y-6" data-id="element-1203">
                  {/* Standard Materials */}
                  <div data-id="element-1204">
                    <h3 className="text-sm font-medium text-stone-700 mb-3" data-id="element-1205">
                      Vælg fra standard
                    </h3>
                    {standardMaterials.length === 0 ? <p className="text-sm text-stone-500 italic" data-id="element-1206">
                        Ingen standard materialer endnu — tilføj dem under
                        Information.
                      </p> : <div className="flex flex-wrap gap-2" data-id="element-1207">
                        {standardMaterials.map(preset => <motion.button key={preset.id} type="button" whileTap={{
                    scale: 0.95
                  }} onClick={() => handleAddPresetMaterial(preset)} className="px-3 py-1.5 rounded-lg bg-stone-50 border border-stone-200 text-sm font-medium text-stone-700 hover:bg-teal-50 hover:border-teal-300 hover:text-teal-700 transition-all flex items-center gap-2" data-id="element-1208">
                            <PackageIcon className="w-4 h-4" data-id="element-1209" />
                            {preset.name}
                            {preset.price !== undefined && <span className="text-stone-500 font-normal" data-id="element-1210">
                                · {preset.price.toLocaleString('da-DK')} kr
                              </span>}
                          </motion.button>)}
                      </div>}
                  </div>

                  <div className="pt-4 border-t border-stone-100" data-id="element-1211">
                    <h3 className="text-sm font-medium text-stone-700 mb-3" data-id="element-1212">
                      Tilføj manuelt
                    </h3>
                    <div className="flex flex-col sm:flex-row gap-4" data-id="element-1213">
                      <input type="text" value={materialInput} onChange={e => setMaterialInput(e.target.value)} onKeyPress={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddMaterial();
                    }
                  }} placeholder="Skriv materiale navn..." className="flex-1 px-4 py-3 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-1214" />
                      <input type="number" step="0.01" value={materialPriceInput} onChange={e => setMaterialPriceInput(e.target.value)} onKeyPress={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddMaterial();
                    }
                  }} placeholder="Pris (kr)" className="w-full sm:w-40 px-4 py-3 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-1215" />
                      <Button type="button" onClick={handleAddMaterial} variant="primary" className="flex items-center justify-center gap-2 py-3 px-6" disabled={!materialInput.trim()} data-id="element-1216">
                        <PlusIcon className="w-5 h-5" data-id="element-1217" />
                        Tilføj
                      </Button>
                    </div>
                  </div>

                  {materialPriceInput && materialPrice && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.2
              }} className="bg-green-50 border border-green-200 rounded-xl p-4" data-id="element-1218">
                      <div className="flex items-center justify-between text-base" data-id="element-1219">
                        <span className="text-green-800" data-id="element-1220">
                          Nuværende materialer pris:
                        </span>
                        <span className="font-medium text-green-900" data-id="element-1221">
                          {parseFloat(materialPrice).toLocaleString('da-DK', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}{' '}
                          kr
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-base mt-2" data-id="element-1222">
                        <span className="text-green-800" data-id="element-1223">Tilføjelse:</span>
                        <span className="font-medium text-green-900" data-id="element-1224">
                          +
                          {parseFloat(materialPriceInput).toLocaleString('da-DK', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}{' '}
                          kr
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-lg mt-3 pt-3 border-t border-green-300" data-id="element-1225">
                        <span className="font-semibold text-green-900" data-id="element-1226">
                          Ny total:
                        </span>
                        <span className="font-bold text-green-900" data-id="element-1227">
                          {(parseFloat(materialPrice) + parseFloat(materialPriceInput)).toLocaleString('da-DK', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}{' '}
                          kr
                        </span>
                      </div>
                    </motion.div>}
                </motion.div>}
            </AnimatePresence>

            {materials.length > 0 && <div className="mt-6" data-id="element-1228">
                <h4 className="text-sm font-medium text-stone-700 mb-3" data-id="element-1229">
                  Tilføjede materialer ({materials.length})
                </h4>
                <ul className="space-y-2" data-id="element-1230">
                  {materials.map((material, index) => <li key={index} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg border border-stone-200" data-id="element-1231">
                      <span className="text-sm text-stone-700" data-id="element-1232">{material}</span>
                      <button type="button" onClick={() => handleRemoveMaterial(index)} className="text-stone-400 hover:text-red-500 transition-colors p-1 flex items-center gap-1" data-id="element-1233">
                        <XIcon className="w-4 h-4" data-id="element-1234" />
                        Fjern
                      </button>
                    </li>)}
                </ul>
              </div>}
          </div>

          {/* Noter */}
          <div className="bg-white rounded-2xl border border-stone-200 p-6 sm:p-8 shadow-sm" data-id="element-1235">
            <h2 className="text-xl font-semibold text-stone-900 mb-6" data-id="element-1236">Noter</h2>
            <RichTextEditor label="Interne noter" value={subTasks} onChange={setSubTasks} placeholder="Tilføj interne noter her..." rows={4} data-id="element-1237" />
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-end gap-4 pt-4 border-t border-stone-200" data-id="element-1238">
            <Button type="button" variant="secondary" onClick={onBack} className="w-full sm:w-auto py-3 px-6" data-id="element-1239">
              Annuller
            </Button>
            <Button type="submit" variant="primary" className="w-full sm:w-auto py-3 px-8" data-id="element-1240">
              Gem opgave
            </Button>
          </div>
        </form>
      </div>
    </div>;
}